import { isUndefined } from "lodash";

export enum ModelEventAction {
	INIT = "init",
	ADD = "add",
	UPDATE = "update",
	DELETE = "delete",
	REPLACE = "replace",
	MERGE = "merge",
	HIDE = "hide",
	VISIBLE = "visible",
	REGENERATE = "regenerateIndexes",
	STATE = "regenerateState",
	META = "meta",
}

export interface ModelSubscribeEvent {
	modelName?: string;
	action: ModelEventAction;
	id: ModelId;
	data?: JSONLike;
	/**
	 * Position of the model inside the subscription window. Sent only with
	 * `add`, when the server rebuilt the window and the order could drift.
	 */
	index?: number;
}

export type JSONLike =
	| string
	| number
	| boolean
	| null
	| JSONLike[]
	| { [key: string]: JSONLike };

export type ModelId = number | string;

export type ModelPrototype = {
	[key: string]: JSONLike;
};

export type ModelEventConstructorConfig = {
	onUpdate?(state: {
		metadataState: ModelPrototype;
		models: JSONLike[];
	}): void | Promise<void>;
	onSystemEvent?(state: ModelSubscribeEvent): void | Promise<void>;
	/**
	 * Page size of this subscribe. Used when INIT has `count` but no
	 * `windowSize` (older gateway): hydrate ADDs are min(count, limit).
	 */
	hydrateWindowSize?: number;
};

function eventTotal(data?: JSONLike): number | undefined {
	if (!data || typeof data !== "object" || Array.isArray(data)) {
		return undefined;
	}
	const { count } = data as { count?: unknown };
	return typeof count === "number" && Number.isFinite(count)
		? count
		: undefined;
}

function eventWindowSize(data?: JSONLike): number | undefined {
	if (!data || typeof data !== "object" || Array.isArray(data)) {
		return undefined;
	}
	const { windowSize } = data as { windowSize?: unknown };
	return typeof windowSize === "number" &&
		Number.isFinite(windowSize) &&
		windowSize >= 0
		? Math.floor(windowSize)
		: undefined;
}

export class ModelEventConstructor {
	private config: Required<ModelEventConstructorConfig>;

	private modelState: Map<ModelId, JSONLike> = new Map();

	private modelHide: Map<ModelId, JSONLike> = new Map();

	private lastModels: JSONLike[] = [];

	private metadataTotal: number | undefined;

	private lastEmittedTotal: number | undefined;

	/**
	 * Gateway splits INIT and the paged window into several WS batches
	 * once `ids.length >= batchSize`. Those hydrate ADDs must not bump
	 * the tab badge — they are already inside INIT `count`.
	 */
	private hydrateAddsRemaining = 0;

	constructor(config: ModelEventConstructorConfig) {
		this.config = {
			onUpdate: config.onUpdate || (() => {}),
			onSystemEvent: config.onSystemEvent || (() => {}),
			hydrateWindowSize: config.hydrateWindowSize ?? 0,
		};
	}

	private resolveHydrateWindow(
		total: number | undefined,
		data?: JSONLike,
	): number {
		const fromEvent = eventWindowSize(data);
		if (fromEvent !== undefined) return fromEvent;
		const limit = this.config.hydrateWindowSize;
		if (
			typeof total === "number" &&
			typeof limit === "number" &&
			limit >= 0
		) {
			return Math.min(total, limit);
		}
		return 0;
	}

	public onEvent(events: ModelSubscribeEvent[]): void {
		let dirty = false;
		// INIT/STATE/REGENERATE hydrate the window. ADD/DELETE in that same
		// batch must not bump the tab total — those rows are already counted.
		let hydrating = false;
		for (const event of events) {
			switch (event.action) {
				case ModelEventAction.INIT:
					this.modelState.clear();
					this.modelHide.clear();
					hydrating = true;
					{
						const total = eventTotal(event.data);
						if (total !== undefined) this.metadataTotal = total;
						this.hydrateAddsRemaining = this.resolveHydrateWindow(
							total,
							event.data,
						);
					}
					this?.config?.onSystemEvent(event);
					dirty = true;
					break;
				case ModelEventAction.REGENERATE:
					dirty = true;
					hydrating = true;
					{
						const windowSize = eventWindowSize(event.data);
						if (windowSize !== undefined) {
							this.hydrateAddsRemaining = windowSize;
						}
					}
					this?.config?.onSystemEvent(event);
					this.modelHide.clear();
					break;
				case ModelEventAction.STATE:
					this.modelState.clear();
					this.modelHide.clear();
					hydrating = true;
					{
						const total = eventTotal(event.data);
						if (total !== undefined) this.metadataTotal = total;
						this.hydrateAddsRemaining = this.resolveHydrateWindow(
							total,
							event.data,
						);
					}
					this?.config?.onSystemEvent(event);
					dirty = true;
					break;
				case ModelEventAction.REPLACE:
				case ModelEventAction.ADD: {
					const isNew = !this.modelState.has(event.id);
					const isHydrateAdd =
						hydrating || this.hydrateAddsRemaining > 0;
					dirty = this.onAdd(event) || dirty;
					if (isNew && this.modelState.has(event.id)) {
						if (this.hydrateAddsRemaining > 0) {
							this.hydrateAddsRemaining -= 1;
						}
						if (!isHydrateAdd && this.metadataTotal !== undefined) {
							this.metadataTotal += 1;
							dirty = true;
						}
					}
					break;
				}
				case ModelEventAction.MERGE:
				case ModelEventAction.UPDATE:
					dirty = this.onUpdate(event) || dirty;
					break;

				case ModelEventAction.HIDE:
					dirty = this.onHide(event) || dirty;
					break;

				case ModelEventAction.VISIBLE:
					dirty = this.onVisible(event) || dirty;
					break;
				case ModelEventAction.DELETE: {
					const existed = this.modelState.has(event.id);
					dirty = this.onDelete(event) || dirty;
					if (
						!hydrating &&
						existed &&
						this.metadataTotal !== undefined
					) {
						this.metadataTotal = Math.max(
							0,
							this.metadataTotal - 1,
						);
						dirty = true;
					}
					break;
				}
				case ModelEventAction.META: {
					const total = eventTotal(event.data);
					if (total !== undefined && total !== this.metadataTotal) {
						this.metadataTotal = total;
						dirty = true;
					}
					this?.config?.onSystemEvent(event);
					break;
				}
				default:
					this?.config?.onSystemEvent(event);
					break;
			}
		}
		this.syncTotalToWindow();
		const total = this.metadataTotal ?? this.modelState.size;
		if (!dirty && this.lastEmittedTotal === total) {
			return;
		}

		const models = Array.from(this.modelState.values());
		// Keep the previous window on screen until hydrate ADDs arrive.
		// INIT/REGENERATE clear or delete the old ids first; emitting []
		// here is what made the clients table flash "no data" on sort.
		if (models.length === 0 && this.hydrateAddsRemaining > 0) {
			return;
		}
		const prev = this.lastModels;
		const modelsSame =
			prev.length === models.length &&
			prev.every((item, index) => item === models[index]);
		if (modelsSame && this.lastEmittedTotal === total) {
			return;
		}
		this.lastModels = models;
		this.lastEmittedTotal = total;
		this?.config?.onUpdate({
			metadataState: {
				total,
			},
			models,
		});
	}

	/** INIT/regenerate уже очистил окно, а его ADD-чанки ещё не пришли. */
	public isHydrating(): boolean {
		return this.hydrateAddsRemaining > 0;
	}

	/** Известен ли id окну (видимый или скрытый). */
	public has(id: ModelId): boolean {
		return this.modelState.has(id) || this.modelHide.has(id);
	}

	setHydrateWindowSize(limit?: number): void {
		if (typeof limit !== "number" || limit < 0) return;
		const prev = this.config.hydrateWindowSize;
		this.config.hydrateWindowSize = limit;
		if (limit > prev && this.modelState.size > 0) {
			const extra = Math.max(0, limit - this.modelState.size);
			this.hydrateAddsRemaining = Math.max(
				this.hydrateAddsRemaining,
				extra,
			);
		}
	}

	/**
	 * While the first page is not full, the window is the complete match set
	 * (INIT 43 vs 38 hydrated rows). Do not clamp when INIT total is already
	 * larger than the page — that is pagination still filling the window.
	 */
	private syncTotalToWindow(): void {
		const { size } = this.modelState;
		const limit = this.config.hydrateWindowSize;
		if (typeof limit !== "number" || limit <= 0) return;
		if (size === 0) return;
		if (size < limit) {
			if (
				this.metadataTotal === undefined ||
				this.metadataTotal <= limit
			) {
				this.metadataTotal = size;
				this.hydrateAddsRemaining = 0;
			}
		} else if (this.metadataTotal !== undefined) {
			this.metadataTotal = Math.max(this.metadataTotal, size);
		}
	}

	private onHide(event: ModelSubscribeEvent): boolean {
		const { id } = event;
		if (!id) return false;
		if (this.modelState.has(id)) {
			const exist = this.modelState.get(id);
			if (exist) {
				this.modelHide.set(id, exist);
				this.modelState.delete(id);
				return true;
			}
		}
		return false;
	}

	private onVisible(event: ModelSubscribeEvent): boolean {
		const { id } = event;
		if (!id) return false;
		if (this.modelHide.has(id)) {
			const exist = this.modelHide.get(id);
			if (exist) {
				this.modelState.set(id, exist);
				this.modelHide.delete(id);
				return true;
			}
		}
		return false;
	}

	private onUpdate(event: ModelSubscribeEvent): boolean {
		const { data, id } = event;
		if (isUndefined(data)) return false;

		if (this.modelHide.has(id)) {
			if (this.modelHide.get(id) === data) return false;
			this.modelHide.set(id, data);
			return true;
		}

		if (this.modelState.get(id) === data) return false;
		this.modelState.set(id, data);
		return true;
	}

	private onDelete(event: ModelSubscribeEvent): boolean {
		const { id } = event;
		const existed = this.modelState.delete(id);
		this.modelHide.delete(id);
		return existed;
	}

	private onAdd(event: ModelSubscribeEvent): boolean {
		const { id, data, index } = event;
		if (isUndefined(data)) return false;
		if (this.modelHide.has(id)) {
			this.modelHide.set(id, data);
			return true;
		}

		// A Map keeps insertion order, so without a hint a new model always lands
		// at the tail of the window. Tables cut the list down to their own limit
		// from the front, which kept such a model invisible until a reload. The
		// server sends its position in the server-side order, so honour it.
		if (
			typeof index === "number" &&
			index >= 0 &&
			index < this.modelState.size &&
			!this.modelState.has(id)
		) {
			const entries = Array.from(this.modelState.entries());
			entries.splice(index, 0, [id, data]);
			this.modelState = new Map(entries);
			return true;
		}

		if (this.modelState.get(id) === data) return false;
		this.modelState.set(id, data);
		return true;
	}
}
